/**
 * Centralised Firestore collection paths.
 *
 * Every path collection layout. Most collections live
 * under `clubs/{clubId}/...` as subcollections — the React app must use the
 * same paths or the two clients will read/write different data.
 */

export const ROOT_CLUBS = 'clubs' as const
export const ROOT_MAIL = 'mail' as const
/**
 * Platform-global invitation email templates — admin-authored, picked at
 * preview-token send time. Top-level (shared across clubs), admin-only.
 * See `core/models/email-template.ts`.
 */
export const ROOT_EMAIL_TEMPLATES = 'email_templates' as const

/**
 * Platform-global outreach-message templates — plain-text WhatsApp / Instagram
 * messages the admin edits in Plantillas and sends from the token drawer. One
 * doc per channel (`outreach_message_templates/whatsapp` | `…/instagram`).
 * Admin-only (`isAdmin()`). See `core/models/outreach-message-template.ts`.
 */
export const ROOT_OUTREACH_MESSAGE_TEMPLATES = 'outreach_message_templates' as const

export const clubDoc = (clubId: string) => `${ROOT_CLUBS}/${clubId}`

export const clubSubcollections = {
  bookings: (clubId: string) => `${clubDoc(clubId)}/bookings`,
  /** Soft claims on a court while a REMOTE booker finishes — CF-written only. */
  bookingHolds: (clubId: string) => `${clubDoc(clubId)}/bookingHolds`,
  /**
   * Read-optimised projection of every `bookings.payments[]` entry — one
   * doc per money movement, keyed by the entry id, indexed by `paidAt`.
   * Written ONLY by the `on_booking_written` Cloud Function (the booking
   * stays source of truth). Powers the Ingresos income ledger's true
   * payment-date queries. See docs/payments-ledger.md (#165).
   */
  payments: (clubId: string) => `${clubDoc(clubId)}/payments`,
  /**
   * First-class ancillary sales (Ventas) — balls, grips, rental gear,
   * drinks sold at the desk, with or without a linked booking. No
   * inventory in v1. See docs/product/spec/clubmanager/15-extras-and-sales.md.
   */
  sales: (clubId: string) => `${clubDoc(clubId)}/sales`,
  /**
   * Ventas product catalogue (spec/clubmanager/15) — a club-defined hierarchy of
   * sellable items. **NOT inventory**: no stock, no availability — just
   * `name` + `defaultPrice` shortcuts the desk taps, price overridable
   * per-sale. Lines are copied by value into `sales` at sale time
   * (BR-7), so editing/deleting a product never alters past sales.
   */
  catalogCategories: (clubId: string) =>
    `${clubDoc(clubId)}/catalog_categories`,
  catalogProducts: (clubId: string) =>
    `${clubDoc(clubId)}/catalog_products`,
  recurringSeries: (clubId: string) => `${clubDoc(clubId)}/recurring_series`,
  /**
   * Torneos — a three-tier document tree (spec/clubmanager/19):
   *   `tournaments/{tid}` (container) → `categories/{cid}` → `matches/{mid}`.
   * Every mutable part is its own doc so it can be edited in isolation and two
   * courts can enter scores without clobbering each other. Client-CRUD; see
   * core/models/tournament.ts.
   */
  tournaments: (clubId: string) => `${clubDoc(clubId)}/tournaments`,
  /** A tournament's categories (the unit of competition; own format + entrants). */
  tournamentCategories: (clubId: string, tournamentId: string) =>
    `${clubDoc(clubId)}/tournaments/${tournamentId}/categories`,
  /** A category's matches (the hot, per-court, per-score write unit). */
  tournamentMatches: (clubId: string, tournamentId: string, categoryId: string) =>
    `${clubDoc(clubId)}/tournaments/${tournamentId}/categories/${categoryId}/matches`,
  /** Torneo entry-fee payments (one doc per money movement) — CF-projected into
   *  the `payments` income ledger with `source:'tournament'`. */
  tournamentPayments: (clubId: string) => `${clubDoc(clubId)}/tournamentPayments`,
  courts: (clubId: string) => `${clubDoc(clubId)}/courts`,
  crmPlayers: (clubId: string) => `${clubDoc(clubId)}/crm_players`,
  /**
   * Player credit ledger (spec/clubmanager/18) — append-only wallet entries,
   * one per money/credit movement. Spend/refund/expiry entries are written
   * ONLY by Cloud Functions; the derived balance lives on
   * `crm_players/{id}.walletSummary`. See core/models/wallet.ts.
   */
  walletEntries: (clubId: string) => `${clubDoc(clubId)}/wallet_entries`,
  /** Club-defined bono pack templates sold into the wallet (spec/clubmanager/18). */
  bonoProducts: (clubId: string) => `${clubDoc(clubId)}/bono_products`,
  /** CF-maintained rollups (e.g. `walletLiability`). Read-only to clients. */
  aggregates: (clubId: string) => `${clubDoc(clubId)}/aggregates`,
  staff: (clubId: string) => `${clubDoc(clubId)}/staff`,
  activityLog: (clubId: string) => `${clubDoc(clubId)}/activity_log`,
  dataOperations: (clubId: string) => `${clubDoc(clubId)}/data_operations`,
  clubFeedback: (clubId: string) => `${clubDoc(clubId)}/club_feedback`,
  /** Stored at `clubs/{clubId}/roles` in (NOT `club_role`). */
  roles: (clubId: string) => `${clubDoc(clubId)}/roles`,
  /** clubmanager-pwa only — feature requests scoped to a club. */
  featureRequests: (clubId: string) => `${clubDoc(clubId)}/feature_requests`,
  /** clubmanager-pwa only — free-form feature suggestions submitted by a club. */
  featureSuggestions: (clubId: string) => `${clubDoc(clubId)}/feature_suggestions`,
  /** Preview-token claim audit records. Append-only; owner-readable. */
  previewClaims: (clubId: string) => `${clubDoc(clubId)}/preview_claims`,
  /** Preview screen-view trail (CF-written). Owner/admin-readable. */
  previewEvents: (clubId: string) => `${clubDoc(clubId)}/preview_events`,
  /**
   * Per-club preview tokens — read-only access links the platform admin
   * generates for prospects looking at a `mockup` club. Multiple tokens per
   * club allowed (one per recipient). See docs/preview-mockup-and-trial-flow.md.
   */
  previewTokens: (clubId: string) => `${clubDoc(clubId)}/preview_tokens`,
} as const

export type ClubSubcollection = keyof typeof clubSubcollections

/**
 * `subscription` and `features` are NOT subcollections — they are nested maps
 * stored on the club document itself (`clubs/{clubId}.subscription`,
 * `clubs/{clubId}.features`). Read them via `ClubsRepository.getById(clubId)`
 * and pass the nested object to `SubscriptionSchema.parse` / `ClubFeaturesSchema.parse`.
 */
export const CLUB_NESTED_FIELDS = {
  subscription: 'subscription',
  features: 'features',
} as const
